import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CalendarRange, Layers3 } from "lucide-react";
import { formatCompact } from "@/lib/format";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const PhaseBadge = ({ phase }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
      phase === "accumulation"
        ? "bg-[#E7F0EA] text-[#2D6A4F]"
        : "bg-[#FBEDD9] text-[#B07514]"
    }`}
  >
    {phase === "accumulation" ? "Saving" : "Drawdown"}
  </span>
);

const Toggle = ({ options, value, onChange, testid }) => (
  <div className="inline-flex rounded-xl bg-[#F5F2EB] p-1" data-testid={testid}>
    {options.map((o) => {
      const active = value === o.id;
      return (
        <button
          key={o.id}
          data-testid={`${testid}-${o.id}`}
          onClick={() => onChange(o.id)}
          className={`relative rounded-lg px-3 py-1.5 text-xs sm:text-sm font-semibold transition-colors duration-300 ${
            active ? "text-white" : "text-[#5C6A64]"
          }`}
        >
          {active && (
            <motion.span
              layoutId={`${testid}-pill`}
              className="absolute inset-0 rounded-lg bg-[#2D6A4F]"
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
            />
          )}
          <span className="relative flex items-center gap-1.5">{o.icon}{o.label}</span>
        </button>
      );
    })}
  </div>
);

export const ScheduleTab = ({ schedule, currency }) => {
  const [granularity, setGranularity] = useState("monthly");
  const [phase, setPhase] = useState("all");

  const rows = useMemo(() => {
    const base = granularity === "annual" ? schedule.annual : schedule.monthly;
    return phase === "all" ? base : base.filter((r) => r.phase === phase);
  }, [schedule, granularity, phase]);

  const fmt = (v) => formatCompact(v, currency);

  const totals = useMemo(() => {
    return rows.reduce(
      (a, r) => ({
        contribution: a.contribution + r.contribution,
        withdrawal: a.withdrawal + r.withdrawal,
        growth: a.growth + r.growth,
      }),
      { contribution: 0, withdrawal: 0, growth: 0 }
    );
  }, [rows]);

  return (
    <div className="bg-white rounded-2xl border border-[#E8E3D9] p-5 sm:p-6 shadow-sm" data-testid="schedule-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <div>
          <h3 className="font-heading text-xl font-bold text-[#1A231F]">Detailed Cashflow Schedule</h3>
          <p className="text-sm text-[#5C6A64] mt-0.5">
            {rows.length.toLocaleString()} {granularity === "annual" ? "years" : "months"} · full period from
            saving to drawdown
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Toggle
            testid="schedule-granularity"
            value={granularity}
            onChange={setGranularity}
            options={[
              { id: "annual", label: "Yearly", icon: <CalendarRange className="h-3.5 w-3.5" /> },
              { id: "monthly", label: "Monthly", icon: <Layers3 className="h-3.5 w-3.5" /> },
            ]}
          />
          <Toggle
            testid="schedule-phase"
            value={phase}
            onChange={setPhase}
            options={[
              { id: "all", label: "All" },
              { id: "accumulation", label: "Saving" },
              { id: "retirement", label: "Drawdown" },
            ]}
          />
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl bg-[#E7F0EA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-[#2D6A4F] font-semibold">Contributed</div>
          <div className="font-mono font-bold text-[#1A231F] text-sm sm:text-base" data-testid="schedule-total-contribution">{fmt(totals.contribution)}</div>
        </div>
        <div className="rounded-xl bg-[#FBEDD9] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-[#B07514] font-semibold">Withdrawn</div>
          <div className="font-mono font-bold text-[#1A231F] text-sm sm:text-base" data-testid="schedule-total-withdrawal">{fmt(totals.withdrawal)}</div>
        </div>
        <div className="rounded-xl bg-[#EAF0F2] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-[#2A6F7F] font-semibold">Growth</div>
          <div className="font-mono font-bold text-[#1A231F] text-sm sm:text-base" data-testid="schedule-total-growth">{fmt(totals.growth)}</div>
        </div>
      </div>

      <div className="overflow-auto max-h-[560px] rounded-xl border border-[#EFEBE2]" data-testid="schedule-table">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-10 bg-[#1E2B25] text-white">
            <tr className="text-left">
              <th className="px-3 py-2.5 font-semibold whitespace-nowrap">Period</th>
              <th className="px-3 py-2.5 font-semibold">Age</th>
              <th className="px-3 py-2.5 font-semibold">Phase</th>
              <th className="px-3 py-2.5 font-semibold text-right">Opening</th>
              <th className="px-3 py-2.5 font-semibold text-right">Contribution</th>
              <th className="px-3 py-2.5 font-semibold text-right">Withdrawal</th>
              <th className="px-3 py-2.5 font-semibold text-right">Growth</th>
              <th className="px-3 py-2.5 font-semibold text-right">Closing</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={i}
                data-testid={`schedule-row-${i}`}
                className={`border-t border-[#F0ECE3] ${i % 2 ? "bg-[#FBF9F5]" : "bg-white"} hover:bg-[#F3F6F2]`}
              >
                <td className="px-3 py-2 font-medium text-[#1A231F] whitespace-nowrap">
                  {granularity === "annual" ? r.calYear : `${MONTHS[r.monthIndex]} ${r.calYear}`}
                </td>
                <td className="px-3 py-2 text-[#5C6A64]">{r.age}</td>
                <td className="px-3 py-2"><PhaseBadge phase={r.phase} /></td>
                <td className="px-3 py-2 text-right font-mono text-[#5C6A64]">{fmt(r.open)}</td>
                <td className="px-3 py-2 text-right font-mono text-[#2D6A4F]">{r.contribution > 0 ? `+${fmt(r.contribution)}` : "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-[#B07514]">{r.withdrawal > 0 ? `−${fmt(r.withdrawal)}` : "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-[#2A6F7F]">{r.growth >= 0 ? `+${fmt(r.growth)}` : `−${fmt(Math.abs(r.growth))}`}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-[#1A231F]">{fmt(r.close)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-[#8A9790] mt-3">
        Monthly figures split each year's contribution, withdrawal and growth evenly across 12 months; yearly
        totals reconcile exactly to the dashboard projection.
      </p>
    </div>
  );
};
